import {
  Box,
  Grid,
  IconButton,
  Switch,
  Typography,
  alpha,
} from "@material-ui/core";
import React, { useState } from "react";
import styled, { css } from "styled-components";
import {
  useDeleteCustomReportTypeMutation,
  useTogglePublicReportTypeMutation,
} from "~/store/mystore/amc.redux";

import AutoSizedButton from "~/components/buttons/autoSizedButton";
import ConfirmDialog from "~/components/dialogs/confirmDialog";
import CopyIcon from "~/icons/copyIcon";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutlined";
import EditFilledIcon from "@material-ui/icons/Edit";
import { Link } from "react-router-dom";
import { PrunedAMCReportType } from "~/typedef/amc/reportType";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";

interface AMCReportTypeCardProps {
  mid: string;
  reportType: PrunedAMCReportType;
}

const iconStyles = css`
  fill: ${({ theme }) => theme.palette.grey["500"]};
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
`;

const DeleteIcon = styled(DeleteOutlineIcon)`
  ${iconStyles}
  fill: ${({ theme }) => theme.palette.error.main};
`;

const EditIcon = styled(EditFilledIcon)`
  ${iconStyles}
`;

const IconButtonWithBorder = styled(IconButton)`
  ${({ theme }) => `border: 1px solid ${theme.palette.border.main}`};
`;

const PublicSwitchBox = styled(Box)`
  font-size: 0.8rem;
`;

const SwitchStyled = styled(Switch)`
  color: ${({ theme }) => theme.palette.secondary.main};
`;

const ReportTag = styled.span<{
  $isCustomReport?: boolean;
}>`
  font-size: 0.8rem;
  ${({ theme, $isCustomReport }) => `
    color: ${
      $isCustomReport
        ? theme.palette.primary.dark
        : theme.palette.secondary.dark
    };
    background: ${alpha(
      $isCustomReport
        ? theme.palette.primary.main
        : theme.palette.secondary.main,
      0.25
    )};
  `}
  padding: 2px 4px;
  border-radius: 4px;
`;

const DescriptionTypography = styled(Typography)`
  min-height: 30%;
  font-size: 14px;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CustomGrid = styled(Grid)`
  height: 100%;
  padding: 20px;
`;

const ActionsGrid = styled(Grid)`
  padding-top: 20px;
`;

const AMCReportTypeCard: React.FC<AMCReportTypeCardProps> = ({
  mid,
  reportType,
}) => {
  const { t } = useTranslation();
  const user = useTypedSelector((state) => state.user);
  const isCustomReport = !!reportType?.mid;

  const [isPublic, setIsPublic] = useState<boolean>(
    reportType?.public ?? false
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);

  const [
    deleteCustomReport,
    {
      isLoading: isDeleteCustomReportLoading,
      isSuccess: isDeleteCustomReportSuccess,
    },
  ] = useDeleteCustomReportTypeMutation();

  const [
    togglePublicReportType,
    { isLoading: isTogglePublicReportTypeLoading },
  ] = useTogglePublicReportTypeMutation();

  const handleDeleteCustomReport = async (reportId: string) => {
    await deleteCustomReport({ reportId });
  };

  const handleUpdatePublic = async (reportId: string) => {
    const newPublicValue = !isPublic;
    await togglePublicReportType({
      reportId,
      updateItem: { public: newPublicValue },
    });
    setIsPublic(newPublicValue);
  };

  if (isDeleteCustomReportSuccess) return null;

  return (
    <Box
      border={1}
      borderColor="border.main"
      borderRadius={12}
      width="100%"
      height="100%"
    >
      <CustomGrid container direction="column" justifyContent="space-between">
        <Grid item container direction="column" spacing={2}>
          <Grid item>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <ReportTag $isCustomReport={isCustomReport}>
                {isCustomReport
                  ? t("amc.reportLibrary.customReportTag")
                  : t("amc.reportLibrary.standardReportTag")}
              </ReportTag>
              <PublicSwitchBox display="flex" alignItems="center">
                <span>{t("amc.customReports.public")}</span>
                {/* standard report is always public and disabled to change */}
                <SwitchStyled
                  checked={isCustomReport ? isPublic : true}
                  onChange={() => handleUpdatePublic(reportType._id)}
                  disabled={isTogglePublicReportTypeLoading || !isCustomReport}
                />
              </PublicSwitchBox>
            </Box>
          </Grid>
          <Grid item>
            <Typography variant="h2">
              {isCustomReport
                ? reportType?.name
                : t(`amc.${reportType?.name}.label`)}
            </Typography>
          </Grid>
          <Grid item>
            <DescriptionTypography variant="body1">
              {isCustomReport
                ? reportType?.description
                : t(`amc.${reportType?.name}.description`)}
            </DescriptionTypography>
          </Grid>
        </Grid>
        <ActionsGrid item container direction="row-reverse">
          <Grid
            item
            container
            xs={9}
            justifyContent="flex-end"
            alignItems="center"
          >
            {reportType?.mid && user?._id === reportType?.userId && (
              <IconButton
                title={t("amc.reportLibrary.editReportType")}
                component={Link}
                to={{
                  pathname: `/mystores/amc/customreport`,
                  search: `?store=${mid}`,
                  state: {
                    reportTypeId: reportType?._id,
                  },
                }}
              >
                <EditIcon />
              </IconButton>
            )}
            {reportType?.mid && (
              <IconButton
                title={t("amc.reportLibrary.copyReportType")}
                component={Link}
                to={{
                  pathname: `/mystores/amc/customreport`,
                  search: `?store=${mid}`,
                  state: {
                    reportTypeId: reportType?._id,
                    onlyCodeDisplay: true,
                  },
                }}
              >
                <CopyIcon />
              </IconButton>
            )}

            <AutoSizedButton
              variant="outlined"
              component={Link}
              to={`/mystores/amc/schedulereport?store=${mid}&reportType=${reportType?._id}`}
            >
              {t("amc.reportLibrary.scheduleReportType")}
            </AutoSizedButton>
          </Grid>
          <Grid item xs={3}>
            {reportType?.mid && user?._id === reportType?.userId && (
              <IconButtonWithBorder
                onClick={() => setShowDeleteConfirm(true)}
                title={t("amc.reportLibrary.deleteReportType")}
                disabled={isDeleteCustomReportLoading}
              >
                <DeleteIcon />
              </IconButtonWithBorder>
            )}
          </Grid>
        </ActionsGrid>
      </CustomGrid>
      <ConfirmDialog
        open={showDeleteConfirm}
        title={t("generic.confirmTitle")}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          handleDeleteCustomReport(reportType._id);
        }}
        content={
          <Typography variant="body1">
            {t("amc.reportLibrary.deleteReportTypeConfirm", {
              reportName: reportType?.name,
            })}
          </Typography>
        }
      />
    </Box>
  );
};

export default AMCReportTypeCard;
